import { useEffect } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { UsersTable } from './UsersTable';
import { PlansTable } from './PlansTable';
import { getAdminToken, clearAdminToken } from '../utils/auth';
import { useAppContext } from '../context/AppContext';
import api from '../utils/apiClient';

const fetcher = (url) => api.get(url).then((res) => res.data);

function StatCard({ label, value }) {
  return (
    <div className="card">
      <p className="text-[10px] uppercase tracking-[0.08em] text-muted">{label}</p>
      <p className="mt-1 text-xl font-semibold text-white">{Number(value || 0).toLocaleString()}</p>
    </div>
  );
}

function AllAppsOverview({ data }) {
  if (!data?.apps) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
      {data.apps.filter((app) => app.hasBackend !== false).map((app) => (
        <div key={app.appSlug} className="card">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: app.color || '#3e7b6f' }} />
            <span className="text-sm font-semibold text-white">{app.appName}</span>
            {!app.success && (
              <span className="text-[10px] text-rose ml-auto">unreachable</span>
            )}
          </div>
          {app.success ? (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-[10px] text-muted">Users</p>
                <p className="text-base font-semibold text-white">{Number(app.summary?.users || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted">Banned</p>
                <p className="text-base font-semibold text-white">{Number(app.summary?.bannedUsers || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted">Tokens</p>
                <p className="text-base font-semibold text-white">{Number(app.summary?.totalTokens || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted">Requests</p>
                <p className="text-base font-semibold text-white">{Number(app.summary?.totalRequests || 0).toLocaleString()}</p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted">{app.error || 'Failed to fetch data'}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function DashboardClient() {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? getAdminToken() : null;
  const { selectedApp } = useAppContext();

  useEffect(() => {
    if (!token) navigate('/login', { replace: true });
  }, [token, navigate]);

  // Only fetch per-app users/usage when a specific app is selected.
  // When "All Apps" is shown, stats come from the overview aggregation below.
  const usersKey = token && selectedApp
    ? `/api/admin/apps/${selectedApp.slug}/users`
    : null;

  const usageKey = token && selectedApp
    ? `/api/admin/apps/${selectedApp.slug}/usage`
    : null;

  const overviewKey = token && !selectedApp ? '/api/admin/apps/overview/all' : null;

  const { data: usersData } = useSWR(usersKey, fetcher);
  const { data: usageData } = useSWR(usageKey, fetcher);
  const { data: overviewData } = useSWR(overviewKey, fetcher);

  // Aggregate summary across all apps when none is selected
  const aggregatedSummary = !selectedApp && overviewData?.apps
    ? overviewData.apps
        .filter((app) => app.success !== false)
        .reduce(
          (acc, app) => ({
            users: acc.users + (app.summary?.users || 0),
            bannedUsers: acc.bannedUsers + (app.summary?.bannedUsers || 0),
            totalTokens: acc.totalTokens + (app.summary?.totalTokens || 0),
            totalRequests: acc.totalRequests + (app.summary?.totalRequests || 0),
          }),
          { users: 0, bannedUsers: 0, totalTokens: 0, totalRequests: 0 }
        )
    : null;

  const displaySummary = aggregatedSummary ?? usageData?.summary;

  useEffect(() => {
    if (usersData?.success === false && usersData.message?.includes('token')) {
      clearAdminToken();
      navigate('/login', { replace: true });
    }
  }, [usersData, navigate]);

  const handleLogout = () => {
    clearAdminToken();
    navigate('/login', { replace: true });
  };

  const appLabel = selectedApp ? selectedApp.name : 'All Apps';

  return (
    <div className="min-h-screen bg-surface text-white flex">
      <Sidebar onLogout={handleLogout} />
      <main className="flex-1 p-6 overflow-y-auto">
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-serif text-white">Admin Overview</h1>
            <p className="text-xs text-muted mt-1">
              {selectedApp
                ? `Showing data for ${selectedApp.name}`
                : 'Viewing aggregated data across all apps'}
            </p>
          </div>
        </header>

        {!selectedApp && overviewData?.apps?.length > 0 && (
          <>
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted mb-3">Per-App Overview</p>
            <AllAppsOverview data={overviewData} />
          </>
        )}

        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard label={`${appLabel} · Users`} value={displaySummary?.users} />
          <StatCard label="Banned" value={displaySummary?.bannedUsers} />
          <StatCard label="Total Tokens" value={displaySummary?.totalTokens} />
          <StatCard label="Requests" value={displaySummary?.totalRequests} />
        </section>

        <section className="mb-6">
          {selectedApp ? (
            <UsersTable users={usersData?.users || []} selectedApp={selectedApp} />
          ) : (
            <div className="card">
              <h2 className="card-title mb-1">Users</h2>
              <p className="text-xs text-muted">Select an app from the sidebar to view and manage its users.</p>
            </div>
          )}
        </section>

        <section className="mt-4">
          <PlansTable plans={usageData?.topUsers || []} />
        </section>
      </main>
    </div>
  );
}
